<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'SupportTicketCreate' }" class="btn btn-primary">
          <i class="ri-add-line"></i>
          新增工單
        </router-link>
      </div>
    </div>

    <div class="page-container">
      <div class="page-toolbar">
        <div class="filter-group search-box">
          <i class="ri-search-line"></i>
          <input
            v-model="searchQuery"
            type="text"
            class="form-control"
            placeholder="搜尋工單號、主題、會員..."
            @input="handleSearch"
          />
        </div>
        <div class="filter-group">
          <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
            <option value="">全部狀態</option>
            <option value="open">待處理</option>
            <option value="in_progress">處理中</option>
            <option value="resolved">已解決</option>
            <option value="closed">已關閉</option>
          </select>
        </div>
        <div class="filter-group">
          <select v-model="filterPriority" class="form-control" @change="handleFilterChange">
            <option value="">全部優先級</option>
            <option value="urgent">緊急</option>
            <option value="high">高</option>
            <option value="medium">中</option>
            <option value="low">低</option>
          </select>
        </div>
        <div class="filter-group">
          <select v-model="filterAssignee" class="form-control" @change="handleFilterChange">
            <option value="">全部負責人</option>
            <option value="mine">我的工單</option>
            <option value="unassigned">未指派</option>
          </select>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p class="error-message">{{ error }}</p>
        <button class="btn btn-secondary" @click="fetchTickets">重試</button>
      </div>

      <div v-else class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>工單號</th>
              <th>主題</th>
              <th>會員</th>
              <th>優先級</th>
              <th>狀態</th>
              <th>負責人</th>
              <th>建立時間</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="tickets.length === 0">
              <td colspan="8" class="empty-state">沒有找到工單</td>
            </tr>
            <tr v-for="ticket in tickets" :key="ticket.id">
              <td>
                <code class="ticket-number">{{ ticket.ticketNumber }}</code>
              </td>
              <td>
                <div class="subject-cell">
                  <span class="subject-text">{{ ticket.subject }}</span>
                  <span v-if="ticket.categoryName || ticket.category" class="text-muted category-text">
                    {{ ticket.categoryName || ticket.category }}
                  </span>
                </div>
              </td>
              <td>
                <span v-if="ticket.memberName">{{ ticket.memberName }}</span>
                <span v-else class="text-muted">-</span>
                <div v-if="ticket.memberPhone" class="text-muted phone-text">{{ ticket.memberPhone }}</div>
              </td>
              <td>
                <span :class="['badge', priorityBadgeClass(ticket.priority)]">
                  {{ priorityLabel(ticket.priority) }}
                </span>
              </td>
              <td>
                <span :class="['badge', statusBadgeClass(ticket.status)]">
                  {{ statusLabel(ticket.status) }}
                </span>
              </td>
              <td>{{ ticket.assignedToName || '-' }}</td>
              <td :title="formatDate(ticket.createdAt)">{{ formatDate(ticket.createdAt) }}</td>
              <td>
                <div class="action-buttons">
                  <router-link
                    :to="{ name: 'SupportTicketDetail', params: { id: ticket.id } }"
                    class="btn btn-sm btn-primary"
                  >
                    <i class="ri-eye-line"></i>
                    詳情
                  </router-link>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <ListPagination
          v-if="pagination && pagination.totalPages > 1"
          :page="pagination.page"
          :total-pages="pagination.totalPages"
          :total="pagination.totalItems"
          @change="changePage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { supportService } from '@/api/modules/support.js';
import authService from '@/helpers/authservice/authService.js';
import ListPagination from '@/components/ListPagination.vue';
import { showError } from '@/utils/swal.js';

const STATUS_LABELS = {
  open: '待處理',
  in_progress: '處理中',
  resolved: '已解決',
  closed: '已關閉'
};

const PRIORITY_LABELS = {
  urgent: '緊急',
  high: '高',
  medium: '中',
  low: '低'
};

export default {
  name: 'SupportTickets',
  components: { ListPagination },
  setup() {
    const { pageTitle } = usePageTitle('客服工單');
    const tickets = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterStatus = ref('');
    const filterPriority = ref('');
    const filterAssignee = ref('');
    const pagination = ref(null);
    const currentPage = ref(1);
    let searchTimeout = null;
    const currentUser = authService.getCurrentUser() || {};
    const currentUserId = currentUser.id || currentUser.accountId || '';

    const statusLabel = (status) => STATUS_LABELS[status] || status || '-';
    const priorityLabel = (priority) => PRIORITY_LABELS[priority] || priority || '-';

    const statusBadgeClass = (status) => {
      const map = {
        open: 'badge-warning',
        in_progress: 'badge-info',
        resolved: 'badge-success',
        closed: 'badge-secondary'
      };
      return map[status] || 'badge-secondary';
    };

    const priorityBadgeClass = (priority) => {
      const map = {
        urgent: 'badge-danger',
        high: 'badge-warning',
        medium: 'badge-info',
        low: 'badge-secondary'
      };
      return map[priority] || 'badge-secondary';
    };

    const formatDate = (value) => {
      if (!value) return '-';
      try {
        return new Date(value).toLocaleString('zh-TW', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch {
        return value;
      }
    };

    const fetchTickets = async () => {
      loading.value = true;
      error.value = null;
      try {
        const params = {
          page: currentPage.value,
          pageSize: 20,
          sortBy: 'createdAt',
          order: 'desc'
        };
        if (searchQuery.value.trim()) params.search = searchQuery.value.trim();
        if (filterStatus.value) params.status = filterStatus.value;
        if (filterPriority.value) params.priority = filterPriority.value;
        if (filterAssignee.value === 'mine' && currentUserId) {
          params.assignedTo = currentUserId;
        } else if (filterAssignee.value === 'unassigned') {
          params.unassigned = true;
        }

        const response = await supportService.listTickets(params);
        if (response?.success && response.data) {
          tickets.value = response.data.items || [];
          pagination.value = response.data.pagination || null;
        } else {
          error.value = response?.message || '獲取工單列表失敗';
        }
      } catch (err) {
        console.error('獲取工單列表失敗:', err);
        error.value = err.message || '獲取工單列表失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout) clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        currentPage.value = 1;
        fetchTickets();
      }, 300);
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchTickets();
    };

    const changePage = (page) => {
      currentPage.value = page;
      fetchTickets();
    };

    onMounted(fetchTickets);
    onBeforeUnmount(() => {
      if (searchTimeout) clearTimeout(searchTimeout);
    });

    return {
      pageTitle,
      tickets,
      loading,
      error,
      searchQuery,
      filterStatus,
      filterPriority,
      filterAssignee,
      pagination,
      fetchTickets,
      handleSearch,
      handleFilterChange,
      changePage,
      statusLabel,
      priorityLabel,
      statusBadgeClass,
      priorityBadgeClass,
      formatDate
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 1100px;
}

.ticket-number {
  font-size: 12px;
  background: #f1f3f5;
  padding: 2px 6px;
  border-radius: 4px;
}

.subject-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.subject-text {
  font-weight: 500;
}

.category-text,
.phone-text {
  font-size: 12px;
}

.text-muted {
  color: #868e96;
}
</style>
